/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/router';
import { Alert, CircularProgress, DialogActions, DialogContent, Grid, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useHookForm } from '@aspendental/shared-forms-web/lib/hooks/useHookForm';
import { Form, MaskedPhoneNumberInput, SingleCheckbox, TextInput } from '@aspendental/shared-forms-web';
import { getSessionId } from '@aspendental/shared-utils/utils';
import { FieldValues } from 'react-hook-form';
import ReactMarkdown from 'react-markdown';
import { getCookie } from 'cookies-next';

import { verifyZipCode } from '@/services/verifyZipCode';
import { dataLayerConfig } from '@/utils';
import TAGButton from '@/components/TAGButton/TAGButton';
import TAGSvgIcon from '@/components/TAGSvgIcon/TAGSvgIcon';
import postQuiz from '@/services/postQuiz';
import { useAppContext } from '@/context';
import { unacceptableInputs } from '@/utils/offensive-words';
import postQuizCC from '@/services/postQuizCC';
import { errorMappingCC } from '@/constants';
import { getSpinnerStyles } from '@/components/DentrinoVirtualTryOn/UserInfo/UserInfo.styles';
import { buildPurposes, getBffOneTrustProxy, PurposeConsent } from '@/services/getBffOneTrustProxy';
import postMarketingData from '@/services/postMarketingData';
import { BffBrandType } from '@/graphql/__generated/sdk';

import { dialogActionsStyles } from '../Quiz.styles';
import {
	APIFormDataAD,
	APIFormDataCC,
	determineQuizEndpointOverwrite,
	getPartialOrFullPath,
	getResultType,
	IStateObject,
	quizGA4DataLayerPush,
	scrollElementIntoView,
	TOption,
	TQuestion,
	TUser,
	TUsers,
} from '../Quiz.helpers';

import { defaultValues, schema } from './UserDetailsForm.helpers';


export interface IUserDetailsForm {
	state: IStateObject;
	setState: (newState: Partial<IStateObject>) => void;
	submitEndpointUrl: string;
	showInDialogOrDrawer: boolean;
	quizType: string;
	clearChoicePoorCreditRedirectPath?: string;
}

interface IUserForm {
	title: string;
	description: string;
	fields: TUsers;
	submitButtonLabel: string;
	legalMessage: string;
	formHelperText?: string;
	fieldsDirection?: 'row' | 'column';
}

interface IUserFormContentsWrapper {
	userForm: IUserForm;
	isSmallScreen: boolean;
}

interface IUserFormActionButtonsWrapper {
	userForm: IUserForm;
	disableSubmitButton: boolean;
	handleBack: () => void;
	hideBackButton: boolean;
	isLoading: boolean;
}

interface ICCResponse {
	session: any;
	lead: {id: string, sfid: string}
	failure_list: []; 
}

const UserFormContentsWrapper = ({ userForm, isSmallScreen }: IUserFormContentsWrapper) => {
	return (
		<Grid container direction="column" justifyContent="center" alignItems="center" gap={2} marginTop="2rem">
			<Grid item>
				<Typography variant="header2" color="text.primary" textAlign="center">
					{userForm.title}
				</Typography>
			</Grid>

			{userForm.description ? (
				<Grid item textAlign="center">
					<Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}>
						{userForm.description}
					</Typography>
				</Grid>
			) : null}

			<Grid item width="100%">
				<Grid
					container
					direction={userForm.fieldsDirection ?? 'column'}
					spacing={2}
					justifyContent="center"
					alignItems={!isSmallScreen && userForm.fieldsDirection === 'row' ? 'stretch' : 'center'}
					marginTop="2.5rem"
					marginBottom="2.5rem"
				>
					{userForm.fields.map((field: TUser) => {
						return (
							<Grid
								item
								width={isSmallScreen || userForm.fieldsDirection === 'row' ? '100%' : '50%'}
								data-private
								key={field.name}
								{...(userForm.fieldsDirection === 'row'
									? {
											xs: 12,
											md: field.span ?? 12,
									  }
									: {})}
							>
								{field.type === 'Text' && (
									<TextInput
										name={field.name}
										label={field.label}
										dataTestId={`user_details_form_element_${field.name}`}
										enforceAlphaNumeric={false}
										required={field.required}
										maxLength={field.maxLength}
									/>
								)}
								{field.type === 'Phone' && (
									<MaskedPhoneNumberInput
										name={field.name}
										label={field.label}
										required={field.required}
									/>
								)}
								{field.type === 'Checkbox' && <SingleCheckbox name={field.name} label={field.label} />}
							</Grid>
						);
					})}
					{userForm?.formHelperText && (
						<Grid item textAlign="center">
							{renderMarkdown(userForm.formHelperText)}
						</Grid>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
};

const renderMarkdown = (text: string) => {
	return (
		<ReactMarkdown
			components={{
				p: ({ children }) => (
					<Typography variant="bodyMediumBook" color="text.secondary">
						{children}
					</Typography>
				),
				span: ({ children }) => (
					<Typography variant="bodyMediumBook" color="text.secondary">
						{children}
					</Typography>
				),
				a: ({ children, href }) => (
					<Link href={href as string} target="_blank">
						<Typography variant="bodyMediumBook" color="text.interactive">
							{children}
						</Typography>
					</Link>
				),
			}}
		>
			{text}
		</ReactMarkdown>
	);
};

const UserFormActionButtonsWrapper = ({
	userForm,
	disableSubmitButton,
	handleBack,
	hideBackButton,
	isLoading
}: IUserFormActionButtonsWrapper) => {
	return (
		<Grid container justifyContent="center" alignItems="center" flexDirection="column" margin="auto">
			{userForm?.legalMessage && (
				<Grid item mx={4} my={2} textAlign="center">
					{renderMarkdown(userForm.legalMessage)}
				</Grid>
			)}
			<Grid container spacing={2} justifyContent="center" alignItems="center">
				{hideBackButton ? null : (
					<Grid item>
						<TAGButton
							size="M"
							variant="tertiaryDefault"
							onClick={handleBack}
							startIcon={<TAGSvgIcon icon={'ChevronLeftFunc'} size={25} />}
						>
							Back
						</TAGButton>
					</Grid>
				)}
				<Grid item>
					<TAGButton size="M" variant="primaryDefault" type="submit" disabled={disableSubmitButton}>
						{isLoading && 
							<CircularProgress size={20} thickness={3.5} sx={{
								...getSpinnerStyles,
								mr: 2,
							  }} />
						}
							{userForm.submitButtonLabel}
					</TAGButton>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default function UserDetailsForm({
	state,
	setState,
	showInDialogOrDrawer = false,
	submitEndpointUrl,
	quizType,
	clearChoicePoorCreditRedirectPath = '',
}: Readonly<IUserDetailsForm>) {
	const { formData } = state;
	const [formResultTracked, setFormResultTracked] = useState(false);
	const [encodedAnswer, setEncodedAnswer] = useState('N/A');
	const [offensiveInputsFound, setOffensiveInputsFound] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const [isLoading, setIsLoading] = useState(false);

	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const { environment, config, stateCodeFromHeaders } = useAppContext();
		const router = useRouter();

	const form = useHookForm({
		schema: schema(config.name, formData),
		defaultValues: defaultValues(config.name, formData),
		mode: 'onBlur',
	});

	useEffect(() => {
		scrollElementIntoView(containerRef);
	}, []);

	useEffect(() => {
		if (!formResultTracked && formData) {
			setFormResultTracked(true);
			const isClearChoice = config.name === 'ClearChoice';

			if (isClearChoice) {
				const ficoQuestion = formData.form.form_questions.find(
					(question) => question.questionId === 'current_credit_score'
				);

				const answerEncodings = [
					{
						value: '740+',
						encoding: '0',
					},
					{
						value: '700-739',
						encoding: '1',
					},
					{
						value: '660-699',
						encoding: '2',
					},
					{
						value: 'Under 660',
						encoding: '3',
					},
					{
						value: 'I intend to self-pay',
						encoding: '5',
					},
					{
						value: 'I don’t know or prefer not to answer',
						encoding: '6',
					},
				];

				if (ficoQuestion) {
					const answer = ficoQuestion.answer as TOption;
					const matchedEncoding = answerEncodings.find((encodedOpt) => encodedOpt.value === answer.value);
					setEncodedAnswer(matchedEncoding ? matchedEncoding.encoding : 'N/A');

					dataLayerConfig({
						event: 'formSubmission',
						form_data: {
							brand: formData.form.brand,
							product_line: formData.form.product_line,
							submission_date: new Date().toISOString(),
							form_title: formData.form.form_title,
							form_description: formData.form.form_description,
							form_type: formData.form.form_type,
							form_result: matchedEncoding?.encoding,
							form_answer: 'N/A',
							form_step: 'Contact Information',
						},
					});
				}
			}
		}
	}, [formResultTracked, config.name, formData, encodedAnswer]);

	if (!formData) {
		return null;
	}

	const formQuestions = formData.form.form_questions;
	const userForm = formData.form.user_form;
	const disableSubmitButton = !form.formState.isValid || form.formState.isSubmitting;

	const handleSubmit = async (fields: FieldValues) => {
		/* determine results based on answers */
		const isPartialPathOrFullPath = getPartialOrFullPath(formQuestions);
		const resultType = getResultType(
			formQuestions,
			isPartialPathOrFullPath,
			config.name,
			userForm.isSignatureEliteResult
		);
		const url =
			environment === 'prod' ? config.services.prod.FORM_EVENTS_URL : config.services.nonprod.FORM_EVENTS_URL;
		const formQuestionsPayload = formQuestions.map((formQuestion: TQuestion) => {
			return {
				question: formQuestion.question,
				answer: Array.isArray(formQuestion.answer)
					? formQuestion.answer?.map((answer) => answer.value).join('|')
					: formQuestion.answer?.value,
			};
		});
		const userFormDetailsData = userForm.fields.map((userField: TUser) => {
			return {
				question: userField.label,
				answer: fields[userField.name] as string,
				unique_label: userField.unique_label,
			};
		});
		await postQuiz(url, {}, {
			form: {
				brand: formData.form.brand,
				product_line: formData.form.product_line,
				form_title: formData.form.form_title,
				form_type: formData.form.form_type,
				form_result: resultType.title,
				session_id: getSessionId() || 'storybook',
				form_channel: formData.form.form_channel,
				form_questions: formQuestionsPayload.concat(userFormDetailsData),
			},
		} as APIFormDataAD);

		quizGA4DataLayerPush(config.name, 'submit', resultType.title, formData, stateCodeFromHeaders);
		void router.push(resultType.resultPage);
	};

	const handleSubmitCC = async (fields: FieldValues) => {

		setIsLoading(true);

		// Validate required fields
		const unacceptableFields = unacceptableInputs([
			{ name: 'first_name', value: (fields['first_name'] ?? '') as string },
			{ name: 'last_name', value: (fields['last_name'] ?? '') as string },
		]);
		if (unacceptableFields.length) {
			setOffensiveInputsFound(true);
			setIsLoading(false);
			return;
		} else {
			setOffensiveInputsFound(false);
		}

		if (fields.zip_code && userForm?.verifyZipCode) {
			const isValidZipCode = await verifyZipCode(fields.zip_code as string);
			if (!isValidZipCode) {
				form.setError('zip_code', { message: 'Valid ZIP Code Required' });
				setIsLoading(false);
				return;
			}
		}

		try {
			const isPartialPathOrFullPath = getPartialOrFullPath(formQuestions);
			const resultType = getResultType(
				formQuestions,
				isPartialPathOrFullPath,
				config.name,
				userForm.isSignatureEliteResult,
				quizType,
				clearChoicePoorCreditRedirectPath
			);

			const questionFields = formQuestions.reduce<{ [key: string]: string | string[] | null }>(
				(acc, question: TQuestion) => {
					acc[question.questionId] = Array.isArray(question.answer)
						? question.answer.map((answer: TOption) => answer.id)
						: (question.answer as TOption).id;
					return acc;
				},
				{}
			);

			const contactFields = userForm.fields.reduce<{ [key: string]: string | null }>((acc, field: TUser) => {
				const fieldName = field.name;
				acc[fieldName] = fieldName in fields ? (fields[fieldName] as string | null) : null;
				return acc;
			}, {});

			const payload = {
				subject: 'leads',
				answers: {
					...questionFields,
					...contactFields,
					tag_session_id: getSessionId(),
				},
			};

			const headers = {
				'x-api-key': process.env.NEXT_PUBLIC_CC_API_KEY ?? '',
				'x-api-sig': process.env.NEXT_PUBLIC_CC_API_SIG ?? '',
				'Content-Type': 'application/json',
			};

			const response = await postQuizCC(submitEndpointUrl, payload as unknown as APIFormDataCC, headers) as ICCResponse;

			if (response?.failure_list?.length) {
				response.failure_list.forEach((errorCode: string) => {
					const error = errorMappingCC[errorCode];
					if (error) {
						form.setError(error.field, { message: error.message });
					}
				});
				setIsLoading(false);
				return;
			}

			// --- OneTrust Consent Proxy Integration ---
			try {
				// Build purposes array from checkbox values
				const purposes = buildPurposes({
					sms_marketing: !!fields.sms_marketing,
					sms_transactional: !!fields.sms_transactional,
				});

				const externalId = response.lead.sfid; // Salesforce lead ID

				const dsDataElements = {
					Email: fields.email,
					Mobile: fields.phone,                    
					Address: fields.address_line_1,            
					City: fields.city,
					FirstName: fields.first_name,
					LastName: fields.last_name,
					DateOfBirth: fields.dob,
					Zip: fields.zip_code,
					State: fields.state,
					leadId: response.lead.id,
				};

				const beffeEndpoint = environment === 'prod' 
				  ? config.services.prod.BEFFE_GRAPHQL_URL 
				  : config.services.nonprod.BEFFE_GRAPHQL_URL;

				const oneTrustPayload = {
					brand: BffBrandType.ClearChoice,
					payload: {
						purposes,
						source: "clearchoice_onetrust_proxy",
						brand: "CLEAR_CHOICE",
						externalId, 
						dsDataElements,
					}
				};

				await getBffOneTrustProxy(
					oneTrustPayload,
					beffeEndpoint,
					config.name,
					'1.0.0'
				);

			} catch (error) {
				console.error('Error sending OneTrust data:', error);
				// Don't block the user flow if OneTrust fails
			}

			dataLayerConfig({
				event: 'formSubmission',
				form_data: {
					brand: formData.form.brand,
					product_line: formData.form.product_line,
					submission_date: new Date().toISOString(),
					form_title: formData.form.form_title,
					form_description: formData.form.form_description,
					form_type: formData.form.form_type,
					form_result: encodedAnswer,
					form_step: 'Submit Quiz',
					lead_id: response.lead.sfid
				},
			});

			if (response?.lead?.id) {

				const marketingSourceCookie = getCookie('marketing_source') as string;
				
				if (marketingSourceCookie) {
				const urlParams = new URLSearchParams(marketingSourceCookie);
				const marketingData: Record<string, any> = {
					session_id: getSessionId(), 
					timestamp: Date.now(),
					domain: 'https://clearchoice.com',
					pathname: router.pathname || '/',
				};

				for (const [key, value] of urlParams.entries()) {
					if (key === 'trans_id') {
					marketingData[key] = [value];
					} else {
					marketingData[key] = value;
					}
				}

				// if the feature flag exists, add document.referrer to the marketingData payload, even if empty
				if (config.featureFlags.enableCcMarketingSourceTransformation) {
					marketingData.referrer = document.referrer
				}

				const payload = {	
					leadId: response.lead.id, 
					marketingData: marketingData,
				};

				const baseUrl = environment === 'prod' ? config.services.prod.CLEARCHOICE_API_URL : config.services.nonprod.CLEARCHOICE_API_URL

				const apiURL = `${baseUrl}/leads/${response.lead.id}/marketing-data`
				
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				postMarketingData(
					determineQuizEndpointOverwrite({
						defaultSubmitEndpointUrl: apiURL,
						brandName: 'ClearChoice',
						enableHydra: config.featureFlags?.enableHydra || false,
						hydraPilotLocations: config.featureFlags?.hydraPilotLocations || [],
					} ),
					payload,
					headers
				);
				}
			}

			setIsLoading(false)
			void router.push(resultType.resultPage);
		} catch (e) {
			setIsLoading(false);
		}
	};

	const handleBack = () => {
		setState({
			formType: 'Questions',
		});
	};

	return (
		<Form
			form={form}
			onSubmit={config.name === 'ClearChoice' ? handleSubmitCC : handleSubmit}
			data-test-id="user_details_form"
		>
			{showInDialogOrDrawer ? (
				<>
					<DialogContent>
						<UserFormContentsWrapper userForm={userForm} isSmallScreen={isSmallScreen} />
						{offensiveInputsFound && (
							<Alert severity="error" variant="flatError" sx={{ mb: 2 }}>
								<Typography color="text.secondary">Inappropriate text detected</Typography>
							</Alert>
						)}
					</DialogContent>
					<DialogActions sx={dialogActionsStyles}>
						<UserFormActionButtonsWrapper
							isLoading={isLoading}
							userForm={userForm}
							disableSubmitButton={disableSubmitButton}
							handleBack={handleBack}
							hideBackButton={!formQuestions.length}
						/>
					</DialogActions>
				</>
			) : (
				<>
					<div ref={containerRef}>
						<UserFormContentsWrapper userForm={userForm} isSmallScreen={isSmallScreen} />
						{offensiveInputsFound && (
							<Alert severity="error" variant="flatError" sx={{ mb: 2 }}>
								<Typography color="text.secondary">Inappropriate text detected</Typography>
							</Alert>
						)}
					</div>
					<UserFormActionButtonsWrapper
						isLoading={isLoading}
						userForm={userForm}
						disableSubmitButton={disableSubmitButton}
						handleBack={handleBack}
						hideBackButton={!formQuestions.length}
					/>
				</>
			)}
		</Form>
	);
}

